import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { toast } from './Toast';
import './VendorFormModal.css';

function VendorFormModal({ vendor, onClose, onSuccess }) {
  const { isCompanyAdmin } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '91',
    email: '',
    address: '',
    vendorType: '',
    branchId: ''
  });
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('91');
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    if (isCompanyAdmin()) fetchBranches();
  }, []);

  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name || '',
        phone: vendor.phone || '91',
        email: vendor.email || '',
        address: vendor.address || '',
        vendorType: vendor.vendorType || '',
        branchId: vendor.branchId || ''
      });

      let fullPhone = vendor.phone || '';
      let code = '91';
      let num = '';
      if (fullPhone === '91' || !fullPhone) {
        code = '91';
        num = '';
      } else if (fullPhone.startsWith('+')) {
        const match = fullPhone.match(/^\+(\d{1,4})/);
        if (match) {
          code = '+' + match[1];
          num = fullPhone.slice(match[0].length);
        }
      } else if (fullPhone.startsWith('91') && fullPhone.length > 2) {
        code = '91';
        num = fullPhone.slice(2);
      } else {
        code = '91';
        num = fullPhone;
      }
      setCountryCode(code);
      setPhoneNumber(num);
    } else {
      setFormData({
        name: '',
        phone: '91',
        email: '',
        address: '',
        vendorType: '',
        branchId: ''
      });
      setCountryCode('91');
      setPhoneNumber('');
    }
  }, [vendor]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/company/branches');
      const data = response.data.data || response.data;
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (['phone', 'alternatePhone'].includes(name)) {
      val = val.replace(/[a-zA-Z]/g, '');
    }
    setFormData(prev => ({ ...prev, [name]: val }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = (combinedPhone) => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!combinedPhone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^[0-9+\-\s()]{7,20}$/.test(combinedPhone)) newErrors.phone = 'Invalid phone format';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (isCompanyAdmin() && !formData.branchId) newErrors.branchId = 'Branch is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(Object.values(newErrors)[0]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const combinedPhone = `${countryCode}${phoneNumber}`;
    if (!validateForm(combinedPhone)) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        phone: combinedPhone,
        branchId: formData.branchId ? parseInt(formData.branchId) : null
      };
      if (vendor) {
        await axios.put(`/vendors/${vendor.id}`, payload);
      } else {
        await axios.post('/vendors', payload);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{vendor ? 'Edit Vendor' : 'Create Vendor'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          {isCompanyAdmin() && (
            <div className="form-group">
              <label htmlFor="branchId">Branch *</label>
              <select id="branchId" name="branchId" value={formData.branchId}
                onChange={handleChange} className={errors.branchId ? 'error' : ''}
                required>
                <option value="">Select Branch</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              {errors.branchId && <span className="error-message">{errors.branchId}</span>}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input type="text" id="name" name="name" value={formData.name}
              onChange={handleChange} className={errors.name ? 'error' : ''}
              placeholder="Enter vendor name" required />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone *</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                id="countryCode"
                name="countryCode"
                placeholder="Code"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value.replace(/[^0-9+]/g, ''))}
                disabled={loading}
                className={errors.phone ? 'error' : ''}
                style={{ width: '80px', flexShrink: 0 }}
                maxLength="5"
                required
              />
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={loading}
                className={errors.phone ? 'error' : ''}
                style={{ flexGrow: 1 }}
                maxLength="15"
                required
              />
            </div>
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input type="email" id="email" name="email" value={formData.email}
              onChange={handleChange} className={errors.email ? 'error' : ''}
              placeholder="Enter email address" />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="vendorType">Vendor Type</label>
            <input type="text" id="vendorType" name="vendorType" value={formData.vendorType}
              onChange={handleChange}
              placeholder="e.g., Supplier, Subcontractor, Equipment Rental" />
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <textarea id="address" name="address" value={formData.address}
              onChange={handleChange} rows="3" placeholder="Enter address" />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (vendor ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VendorFormModal;
